import { useEffect, useState } from 'react'
import { listPlans, deletePlan, MembershipPlan } from '@/api/plans'
import { PlanDialog } from './PlanDialog'
import { AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api-error'
import { Plus, CreditCard, Pencil, PowerOff, CalendarDays, Clock } from 'lucide-react'

function formatCOP(n: number) {
  return `$${Number(n).toLocaleString('es-CO')}`
}

export function PlanList() {
  const [plans, setPlans]             = useState<MembershipPlan[]>([])
  const [loading, setLoading]         = useState(true)
  const [dialogOpen, setDialogOpen]   = useState(false)
  const [editingPlan, setEditingPlan] = useState<MembershipPlan | null>(null)
  const [pendingDelete, setPendingDelete] = useState<MembershipPlan | null>(null)

  useEffect(() => {
    listPlans()
      .then(r => setPlans(r.plans))
      .catch(() => toast.error('Error al cargar planes'))
      .finally(() => setLoading(false))
  }, [])

  function openCreate() { setEditingPlan(null); setDialogOpen(true) }
  function openEdit(p: MembershipPlan) { setEditingPlan(p); setDialogOpen(true) }

  function handleSaved(saved: MembershipPlan) {
    setPlans(prev => {
      const idx = prev.findIndex(p => p.id === saved.id)
      if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next }
      return [...prev, saved]
    })
    setDialogOpen(false)
  }

  async function handleDeactivate(plan: MembershipPlan) {
    try {
      await deletePlan(plan.id)
      setPlans(prev => prev.map(p => p.id === plan.id ? { ...p, is_active: false } : p))
      toast.success('Plan desactivado')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error al desactivar')
    }
  }

  const active   = plans.filter(p => p.is_active)
  const inactive = plans.filter(p => !p.is_active)

  return (
    <div className="flex flex-col gap-5">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-text-primary font-bold text-base leading-tight">Planes de membresía</h2>
          <p className="text-text-muted text-xs mt-1">
            Define los planes que los miembros pueden contratar.
            {!loading && active.length > 0 && (
              <span className="ml-2 font-semibold text-text-primary">{active.length} activo{active.length !== 1 ? 's' : ''}</span>
            )}
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-accent text-background text-sm font-bold px-4 py-2.5 rounded-xl hover:bg-accent-hover transition-colors cursor-pointer shadow-lg shadow-accent/20 shrink-0"
        >
          <Plus className="w-4 h-4" />
          Nuevo plan
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-surface border border-border rounded-2xl p-5 animate-pulse h-40" />
          ))}
        </div>
      ) : plans.length === 0 ? (
        <div className="bg-surface border border-border rounded-2xl p-10 flex flex-col items-center gap-4 text-center">
          <div className="w-14 h-14 rounded-2xl bg-accent/10 border border-accent/20 flex items-center justify-center">
            <CreditCard className="w-7 h-7 text-accent" />
          </div>
          <div>
            <p className="text-text-primary font-semibold text-sm">Sin planes configurados</p>
            <p className="text-text-muted text-xs mt-1 max-w-xs">
              Crea los planes de membresía que ofrecerás a tus miembros: mensual, trimestral, anual, etc.
            </p>
          </div>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 bg-accent text-background text-sm font-bold px-5 py-2.5 rounded-xl hover:bg-accent-hover transition-colors cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            Crear primer plan
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {/* Planes activos */}
          {active.length > 0 && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {active.map(p => (
                <PlanCard key={p.id} plan={p} onEdit={() => openEdit(p)} onDeactivate={() => setPendingDelete(p)} />
              ))}
            </div>
          )}

          {/* Planes inactivos */}
          {inactive.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-text-muted text-[10px] font-semibold uppercase tracking-widest px-1">
                Desactivados
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {inactive.map(p => (
                  <PlanCard key={p.id} plan={p} onEdit={() => openEdit(p)} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <PlanDialog open={dialogOpen} plan={editingPlan} onClose={() => setDialogOpen(false)} onSaved={handleSaved} />

      <AlertDialog open={!!pendingDelete} onOpenChange={() => setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogTitle>¿Desactivar "{pendingDelete?.name}"?</AlertDialogTitle>
          <AlertDialogDescription>
            Los miembros con este plan activo no se verán afectados. El plan dejará de ofrecerse para nuevas membresías.
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { handleDeactivate(pendingDelete!); setPendingDelete(null) }}>
              Desactivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function PlanCard({ plan, onEdit, onDeactivate }: {
  plan: MembershipPlan
  onEdit: () => void
  onDeactivate?: () => void
}) {
  const inactive = !plan.is_active

  return (
    <div className={`bg-surface border rounded-2xl p-5 flex flex-col gap-4 group transition-all duration-200 ${
      inactive
        ? 'border-border opacity-55'
        : 'border-border hover:border-accent/30 hover:shadow-lg hover:shadow-black/10 hover:-translate-y-0.5'
    }`}>

      {/* Top row: icon + status + actions */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
            inactive ? 'bg-border/50 border border-border' : 'bg-accent/10 border border-accent/15'
          }`}>
            <CreditCard className={`w-4 h-4 ${inactive ? 'text-text-muted' : 'text-accent'}`} />
          </div>
          {inactive && (
            <span className="text-[10px] font-semibold text-text-muted bg-background border border-border px-2 py-0.5 rounded-full">
              Inactivo
            </span>
          )}
        </div>
        <div className={`flex items-center gap-1 ${inactive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
          <button
            onClick={onEdit}
            aria-label="Editar"
            className="p-1.5 rounded-lg text-text-muted hover:text-accent hover:bg-accent/10 transition-colors cursor-pointer"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          {onDeactivate && (
            <button
              onClick={onDeactivate}
              aria-label="Desactivar"
              className="p-1.5 rounded-lg text-text-muted hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-500/10 transition-colors cursor-pointer"
            >
              <PowerOff className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Name + description */}
      <div className="flex-1">
        <p className="text-text-primary font-bold text-sm leading-snug">{plan.name}</p>
        {plan.description && (
          <p className="text-text-muted text-[11px] mt-1 line-clamp-2 leading-snug">{plan.description}</p>
        )}
      </div>

      {/* Price */}
      <div>
        <p className={`font-black text-xl leading-tight ${inactive ? 'text-text-muted' : 'text-accent'}`}>
          {formatCOP(Number(plan.price))}
        </p>
        <p className="text-text-muted text-[10px]">COP</p>
      </div>

      {/* Duration + grace */}
      <div className="border-t border-border pt-3 flex items-center gap-3">
        <span className="flex items-center gap-1.5 text-text-muted text-xs">
          <CalendarDays className="w-3.5 h-3.5 shrink-0" />
          {plan.duration_days} días
        </span>
        {plan.grace_days > 0 && (
          <span className="flex items-center gap-1.5 text-text-muted text-xs">
            <Clock className="w-3.5 h-3.5 shrink-0" />
            +{plan.grace_days} gracia
          </span>
        )}
      </div>
    </div>
  )
}
